/* eslint-disable react/jsx-no-useless-fragment */
import moment from "moment";
import { useContext, useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import ReactToPrint from "react-to-print";
import {
  FiDownload,
  FiArrowLeft,
  FiCalendar,
  FiMapPin,
  FiUser,
  FiFileText,
} from "react-icons/fi";
import { AuthContext } from "../../../contexts/AuthContext";
import { api } from "../../../services/api";
import logoImg from "../../../assets/logo-completo.png";

interface Services {
  service_id: string;
  name: string;
}

interface Equipments {
  equipment_id: string;
  grade: string;
  equipment: Equipment;
}

interface Equipment {
  name: string;
}

interface Sector {
  name: string;
}

interface Sectors {
  sector_id: string;
  sector: Sector;
  name: string;
  equipments: Equipments[];
  observation: string;
  images: any;
  show: boolean;
}

interface Costumer {
  id: string;
  name: string;
  address: string;
  number: string;
}

interface ReportImage {
  id: string;
  report_id: string;
  file_name: string;
  created_at: string;
  updated_at: string;
}

interface TechVisitReport {
  costumer: Costumer;
  begin: string;
  end: string;
  observation: string;
  code: number;
  pending: boolean;
  responsible_signature: string;
  technician_signature: string;
  sectors: Sectors[];
  services: Services[];
  technician: Technician;
  responsible: Responsible;
  type: "technical_visit" | "delivery";
  images: (ReportImage | string)[];
}

interface Responsible {
  id: string;
  name: string;
}

interface Technician {
  id: string;
  name: string;
}

function gradeWord(grade) {
  let word = "";

  switch (grade) {
    case "O":
      word = "Ótimo";
      break;
    case "B":
      word = "Bom";
      break;
    case "R":
      word = "Revisado";
      break;
    case "S":
      word = "Substituído";
      break;
    default:
      word = "";
      break;
  }

  return word;
}

export function TechVisitReportPDF() {
  const { user } = useContext(AuthContext);
  const { id } = useParams();
  const [report, setReport] = useState<TechVisitReport>();
  const [loading, setLoading] = useState(true);

  // Estado para preview de imagem
  const [imagePreview, setImagePreview] = useState({ show: false, src: "", alt: "" });

  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    api
      .get(`/techVisitReport/${id}`)
      .then(response => {
        response.data.sectors.map(sector => {
          return Object.assign(sector, { show: false });
        });

        return setReport(response.data);
      })
      .catch(error => {
        console.error("Erro ao carregar relatório:", error);
        // Não define report, deixando undefined para mostrar mensagem de não encontrado
      })
      .finally(() => setLoading(false));
  }, [id]);

  // Função para abrir preview de imagem
  const openImagePreview = useCallback((src: string, alt: string) => {
    setImagePreview({ show: true, src, alt });
  }, []);

  // Função para fechar preview de imagem
  const closeImagePreview = useCallback(() => {
    setImagePreview({ show: false, src: "", alt: "" });
  }, []);

  // Detectar tecla ESC para fechar preview de imagem
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && imagePreview.show) {
        closeImagePreview();
      }
    };

    if (imagePreview.show) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [imagePreview.show, closeImagePreview]);

  const ref = useRef();

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0085BD]" />
          <span className="text-gray-600">Carregando relatório...</span>
        </div>
      </div>
    );
  }

  // Not found state
  if (!report) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center max-w-md">
          <FiFileText className="mx-auto text-gray-400 mb-4" size={48} />
          <h2 className="text-xl font-semibold text-gray-800 mb-2">
            Relatório não encontrado
          </h2>
          <p className="text-gray-500 mb-6">
            O relatório solicitado não existe ou foi removido.
          </p>
          {user && (
            <Link
              to="/tech-visit-reports"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#0085BD] text-white rounded-xl hover:bg-[#006a96] transition-colors"
            >
              <FiArrowLeft size={18} />
              Voltar aos relatórios
            </Link>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 to-gray-200">
      {/* Header fixo com informações e ações */}
      <div className="sticky top-0 z-10 bg-white/95 backdrop-blur-sm shadow-md">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            {/* Info do relatório */}
            <div className="flex-1">
              <h1 className="text-xl font-bold text-[#0085BD] mb-1">
                {report.costumer.name}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                <span className="flex items-center gap-1">
                  <FiCalendar size={14} />
                  {moment(report.begin).format("DD/MM/YYYY")}
                </span>
                <span className="flex items-center gap-1">
                  <FiMapPin size={14} />
                  {report.costumer.address}, {report.costumer.number}
                </span>
                {report.technician && (
                  <span className="flex items-center gap-1">
                    <FiUser size={14} />
                    {report.technician.name}
                  </span>
                )}
                <span className="px-2 py-0.5 bg-gray-100 rounded-full text-xs font-medium">
                  Código: {report.code}
                </span>
              </div>
            </div>

            {/* Botões de ação */}
            <div className="flex items-center gap-3">
              <ReactToPrint
                trigger={() => (
                  <button
                    type="button"
                    className="group flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-[#92C163] to-[#7eb350] text-white rounded-xl hover:from-[#7eb350] hover:to-[#6a9a43] transition-all duration-200 shadow-md hover:shadow-lg"
                  >
                    <FiDownload
                      size={18}
                      className="transition-transform duration-200 group-hover:-translate-y-0.5"
                    />
                    <span className="font-medium">Baixar PDF</span>
                  </button>
                )}
                content={() => ref.current}
                documentTitle={`${report.costumer.name} - ${moment(
                  report.begin,
                ).format("DD/MM/YYYY")}`}
              />
              {user && (
                <Link
                  to="/tech-visit-reports"
                  className="group flex items-center gap-2 px-5 py-2.5 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-all duration-200"
                >
                  <FiArrowLeft
                    size={18}
                    className="transition-transform duration-200 group-hover:-translate-x-0.5"
                  />
                  <span className="font-medium">Voltar</span>
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Área de preview do PDF */}
      <div className="py-8 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Container do PDF com efeito de papel */}
          <div className="relative">
            {/* Sombra decorativa */}
            <div className="absolute inset-0 bg-black/5 rounded-lg transform rotate-1 translate-x-2 translate-y-2" />
            <div className="absolute inset-0 bg-black/5 rounded-lg transform -rotate-1 -translate-x-1 translate-y-1" />

            {/* PDF Content */}
            <div
              className="relative bg-white rounded-lg shadow-2xl overflow-hidden"
              style={{ width: "210mm", margin: "0 auto" }}
            >
              <div className="p-8 print:p-6" ref={ref}>
                <style>{`
                  @media print {
                    @page {
                      size: auto;
                      margin: 0;
                    }
                    body {
                      margin: 0;
                    }
                    * {
                      page-break-inside: avoid !important;
                      page-break-after: avoid !important;
                      page-break-before: avoid !important;
                      break-inside: avoid !important;
                      break-after: avoid !important;
                      break-before: avoid !important;
                    }
                  }
                `}</style>
                {/* Header do PDF */}
                <div className="flex items-center justify-between border-b-2 border-[#0085BD] pb-4 mb-6">
                  <img
                    className="w-[140px] object-contain"
                    src={logoImg}
                    alt="Logo escrito Luvilimp"
                  />
                  <div className="text-right">
                    <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#0085BD]/10 rounded-full">
                      <span className="text-sm font-semibold text-[#0085BD]">
                        Código: {report.code}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Título do Documento */}
                <div className="text-center mb-6">
                  <h1 className="text-xl font-bold text-gray-800 mb-1">
                    {report.type === "delivery"
                      ? "Relatório de Entrega"
                      : "Avaliação de Equipamentos para Higienização"}
                  </h1>
                  <div className="w-24 h-1 bg-[#0085BD] mx-auto rounded-full" />
                </div>

                {/* Informações do Cliente */}
                <div className="bg-gray-50 rounded-lg p-4 mb-6 print:bg-gray-100">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        Cliente
                      </span>
                      <p className="text-base font-semibold text-gray-800">
                        {report.costumer.name}
                      </p>
                    </div>
                    <div>
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        Endereço
                      </span>
                      <p className="text-sm text-gray-700">
                        {report.costumer.address}, {report.costumer.number}
                      </p>
                    </div>
                    <div>
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        Data
                      </span>
                      <p className="text-sm text-gray-700">
                        {moment(report.begin).format("DD/MM/YYYY")}
                      </p>
                    </div>
                    <div>
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        Horário
                      </span>
                      <p className="text-sm text-gray-700">
                        {moment(report.begin).format("HH:mm")} às{" "}
                        {moment(report.end).format("HH:mm")}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Serviços Realizados */}
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-1 h-5 bg-[#0085BD] rounded-full" />
                    <h2 className="text-sm font-bold text-gray-800 uppercase tracking-wide">
                      Serviços Realizados
                    </h2>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {report.services.map(({ service_id, name: service_name }) => (
                      <span
                        key={service_id}
                        className="px-3 py-1 bg-[#0085BD] text-white text-xs font-medium rounded-full"
                      >
                        {service_name}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Setores */}
                <div className="space-y-6 mb-6">
                  {report.sectors.map(
                    ({
                      sector_id,
                      sector,
                      equipments,
                      images,
                      observation: sector_observation,
                    }) => (
                      <div
                        key={sector_id}
                        className="border border-gray-200 rounded-lg overflow-hidden print:break-inside-avoid"
                      >
                        {/* Header do Setor */}
                        <div className="bg-[#0085BD] px-4 py-2">
                          <h3 className="text-white font-semibold">
                            {sector.name}
                          </h3>
                        </div>

                        <div className="p-4">
                          {/* Equipamentos */}
                          {equipments.some(e => e.grade !== "") && (
                            <div className="mb-4">
                              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                                Equipamentos Avaliados
                              </h4>
                              <div className="grid grid-cols-3 gap-2">
                                {equipments.map(
                                  ({ equipment, grade, equipment_id }) =>
                                    grade !== "" && (
                                      <div
                                        key={equipment_id}
                                        className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-lg text-sm"
                                      >
                                        <span className="text-gray-700">
                                          {equipment.name}
                                        </span>
                                        <span
                                          className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                                            grade === "O"
                                              ? "bg-green-100 text-green-700"
                                              : grade === "B"
                                              ? "bg-blue-100 text-blue-700"
                                              : grade === "R"
                                              ? "bg-amber-100 text-amber-700"
                                              : grade === "S"
                                              ? "bg-sky-100 text-sky-700"
                                              : ""
                                          }`}
                                        >
                                          {gradeWord(grade)}
                                        </span>
                                      </div>
                                    ),
                                )}
                              </div>
                            </div>
                          )}

                          {/* Observação do Setor */}
                          {sector_observation && (
                            <div className="mb-4">
                              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                                Observação
                              </h4>
                              <p className="text-sm text-gray-700 bg-amber-50 border-l-4 border-amber-400 px-3 py-2 rounded-r-lg">
                                {sector_observation}
                              </p>
                            </div>
                          )}

                          {/* Imagens do Setor */}
                          {images.length > 0 && (
                            <div>
                              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                                Registros Fotográficos
                              </h4>
                              <div className="grid grid-cols-4 gap-2">
                                {images.map(file => (
                                  <button
                                    key={file.file_name}
                                    type="button"
                                    className="w-full h-[120px] rounded-lg overflow-hidden border border-gray-200 hover:border-[#0085BD] transition-colors focus:outline-none focus:ring-2 focus:ring-[#0085BD]"
                                    onClick={() => openImagePreview(
                                      `https://app-luvilimp.s3.amazonaws.com/techVisitReportSectorImages/${file.file_name}`,
                                      `Registro fotográfico do setor`
                                    )}
                                    title="Clique para ampliar"
                                    aria-label="Ampliar imagem do setor"
                                  >
                                    <img
                                      className="w-full h-full object-cover"
                                      src={`https://app-luvilimp.s3.amazonaws.com/techVisitReportSectorImages/${file.file_name}`}
                                      alt="Imagem do Setor"
                                    />
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ),
                  )}
                </div>

                {/* Observações Gerais */}
                {report.observation && (
                  <div className="mb-6">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-1 h-5 bg-[#0085BD] rounded-full" />
                      <h2 className="text-sm font-bold text-gray-800 uppercase tracking-wide">
                        Observações Gerais
                      </h2>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4 print:bg-gray-100">
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">
                        {report.observation}
                      </p>
                    </div>
                  </div>
                )}

                {/* Imagens da Entrega (apenas para type delivery) */}
                {report.type === "delivery" && report.images && report.images.length > 0 && (
                  <div className="mb-6">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-1 h-5 bg-[#0085BD] rounded-full" />
                      <h2 className="text-sm font-bold text-gray-800 uppercase tracking-wide">
                        Imagens da Entrega
                      </h2>
                    </div>
                    <div className="grid grid-cols-1 gap-3">
                      {report.images.map((image) => {
                        const fileName = typeof image === 'string' ? image : image.file_name;
                        const imageKey = typeof image === 'string' ? image : image.id;

                        return (
                          <button
                            key={imageKey}
                            type="button"
                            className="w-full h-[300px] rounded-lg overflow-hidden border border-gray-200 hover:border-[#0085BD] transition-colors focus:outline-none focus:ring-2 focus:ring-[#0085BD]"
                            onClick={() => openImagePreview(
                              `https://app-luvilimp.s3.amazonaws.com/techVisitReportImages/${fileName}`,
                              `Imagem da entrega`
                            )}
                            title="Clique para ampliar"
                            aria-label="Ampliar imagem da entrega"
                          >
                            <img
                              className="w-full h-full object-cover"
                              src={`https://app-luvilimp.s3.amazonaws.com/techVisitReportImages/${fileName}`}
                              alt="Imagem da Entrega"
                            />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Assinaturas (apenas para technical_visit) */}
                {report.type === "technical_visit" && (
                  <div className="mt-8 pt-6 border-t-2 border-gray-200">
                  <div className="flex items-center gap-2 mb-6">
                    <div className="w-1 h-5 bg-[#0085BD] rounded-full" />
                    <h2 className="text-sm font-bold text-gray-800 uppercase tracking-wide">
                      Assinaturas
                    </h2>
                  </div>
                  <div className="grid grid-cols-2 gap-8">
                    {/* Assinatura do Responsável */}
                    <div className="text-center">
                      <div className="h-[100px] border-b-2 border-gray-400 mb-2 flex items-end justify-center">
                        {report.responsible_signature && (
                          <img
                            className="max-w-[180px] max-h-[90px] object-contain"
                            src={`https://app-luvilimp.s3.amazonaws.com/techVisitReportSignatureImages/${report.responsible_signature}`}
                            alt="Assinatura do Responsável"
                          />
                        )}
                      </div>
                      {report.responsible && (
                        <p className="text-sm font-semibold text-gray-800">
                          {report.responsible.name}
                        </p>
                      )}
                      <p className="text-xs text-gray-500 uppercase tracking-wide">
                        Responsável Cliente
                      </p>
                    </div>

                    {/* Assinatura do Técnico */}
                    <div className="text-center">
                      <div className="h-[100px] border-b-2 border-gray-400 mb-2 flex items-end justify-center">
                        {report.technician_signature && (
                          <img
                            className="max-w-[180px] max-h-[90px] object-contain"
                            src={`https://app-luvilimp.s3.amazonaws.com/techVisitReportSignatureImages/${report.technician_signature}`}
                            alt="Assinatura do Técnico"
                          />
                        )}
                      </div>
                      {report.technician && (
                        <p className="text-sm font-semibold text-gray-800">
                          {report.technician.name}
                        </p>
                      )}
                      <p className="text-xs text-gray-500 uppercase tracking-wide">
                        Responsável Técnico
                      </p>
                    </div>
                  </div>
                </div>
                )}

                {/* Responsável (apenas para delivery) */}
                {report.type === "delivery" && report.technician && (
                  <div className="mt-8 pt-6 border-t-2 border-gray-200">
                    <div className="flex items-center gap-2 mb-6">
                      <div className="w-1 h-5 bg-[#0085BD] rounded-full" />
                      <h2 className="text-sm font-bold text-gray-800 uppercase tracking-wide">
                        Responsável
                      </h2>
                    </div>
                    <div className="text-center">
                      <p className="text-base font-semibold text-gray-800">
                        {report.technician.name}
                      </p>
                      <p className="text-sm text-gray-500 mt-1">
                        Responsável pela Entrega
                      </p>
                    </div>
                  </div>
                )}

                {/* Rodapé */}
                <div className="mt-8 pt-4 border-t border-gray-200 text-center">
                  <p className="text-xs text-gray-400">
                    Documento gerado em {moment().format("DD/MM/YYYY [às] HH:mm")} •
                    Luvilimp - Soluções em Higienização
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de Preview de Imagem */}
      {imagePreview.show && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="relative max-w-4xl max-h-full">
            {/* Botão Fechar */}
            <button
              type="button"
              onClick={closeImagePreview}
              className="absolute -top-10 right-0 text-white hover:text-gray-300 transition-colors z-10"
              aria-label="Fechar preview"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Imagem */}
            <button
              type="button"
              className="p-0 border-0 bg-transparent focus:outline-none"
              onClick={closeImagePreview}
              aria-label="Fechar preview da imagem"
            >
              <img
                src={imagePreview.src}
                alt={imagePreview.alt}
                className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
                style={{ cursor: 'zoom-out' }}
              />
            </button>

            {/* Overlay clicável para fechar */}
            <button
              type="button"
              className="absolute inset-0 -z-10 bg-transparent border-0 focus:outline-none"
              onClick={closeImagePreview}
              aria-label="Fechar preview"
              tabIndex={-1}
            />

            {/* Legenda */}
            {imagePreview.alt && (
              <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-center py-2 px-4 rounded-b-lg">
                {imagePreview.alt}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
